/**
 * @file src/api/dictionary.js
 */
import api from './client';

class DictionaryEngine {
  /**
   * 📥 GET PUBLIC
   * Logic: Returns null on failure to allow Orchestrator a silent fallback.
   */
  static async getPublic(path) {
    try {
      const response = await api.get(`/assets/${path}`);
      return response.data.data || response.data;
    } catch (error) {
      // Fallback: support callers that pass "*.json" while API namespaces omit extension.
      if (typeof path === 'string' && path.endsWith('.json')) {
        try {
          const response = await api.get(`/assets/${path.replace(/\.json$/i, '')}`);
          return response.data.data || response.data;
        } catch {
          return null;
        }
      }
      return null;
    }
  }

  static async getAdmin(coordinate) {
    const { category, namespace } = coordinate;
    try {
      const response = await api.get(`/admin/assets/${category}/${namespace}`);
      const raw = response.data?.data || response.data || {};
      return raw.fullObject || raw.data || raw;
    } catch (err) { return {}; }
  }

  static async list() {
    try {
      const response = await api.get('/admin/assets/list');
      return response.data?.data || [];
    } catch (err) { return []; }
  }

  static async commit(payload) {
    const response = await api.post('/admin/assets/update', payload);
    return response.data;
  }

  // New dictionary service endpoints
  static async listCategory(category, params = {}) {
    const response = await api.get(`/dictionary/${category}`, { params });
    return response.data?.data || response.data;
  }

  static async createCategoryEntry(category, payload = {}) {
    const response = await api.post(`/dictionary/${category}`, payload);
    return response.data?.data || response.data;
  }

  static async updateCategoryEntry(category, entryId, payload = {}) {
    const response = await api.put(`/dictionary/${category}/${entryId}`, payload);
    return response.data?.data || response.data;
  }

  static async patchCategoryStatus(category, entryId, payload = {}) {
    const response = await api.patch(`/dictionary/${category}/${entryId}/status`, payload);
    return response.data?.data || response.data;
  }

  static async deleteCategoryEntry(category, entryId) {
    const response = await api.delete(`/dictionary/${category}/${entryId}`);
    return response.data?.data || response.data;
  }

  static async getCategoryEntry(category, entryId, params = {}) {
    const response = await api.get(`/dictionary/${category}/${entryId}`, { params });
    return response.data?.data || response.data;
  }

  static async bulkImport(items = []) {
    const response = await api.post('/dictionary/bulk-import', { items });
    return response.data?.data || response.data;
  }
  static async importEntries(payload = {}) {
    const normalized = Array.isArray(payload) ? { items: payload } : payload;
    const response = await api.post('/dictionary/import', normalized);
    return response.data?.data || response.data;
  }
  static async exportEntries(params = {}) {
    const response = await api.get('/dictionary/export', { params });
    return response.data?.data || response.data;
  }

  static async auditLogs(params = {}) {
    const response = await api.get('/dictionary/audit-logs', { params });
    return response.data?.data || response.data;
  }

  static async createMapping(payload = {}) {
    const response = await api.post('/dictionary/mapping', payload);
    return response.data?.data || response.data;
  }

  static async getMapping(categoryId, targetId, params = {}) {
    const response = await api.get(`/dictionary/mapping/${categoryId}/${targetId}`, { params });
    return response.data?.data || response.data;
  }

  static async putHierarchy(payload = {}) {
    const response = await api.put('/dictionary/hierarchy', payload);
    return response.data?.data || response.data;
  }

  static async version() {
    const response = await api.get('/dictionary/version');
    return response.data?.data || response.data;
  }

  static async publish() {
    const response = await api.post('/dictionary/publish', {});
    return response.data?.data || response.data;
  }

  static async clearCache() {
    const response = await api.post('/dictionary/cache/clear', {});
    return response.data?.data || response.data;
  }

  static async seedDefault() {
    const response = await api.post('/dictionary/seed/default', {});
    return response.data?.data || response.data;
  }

  static async nlpUnrecognized(params = {}) {
    const response = await api.get('/dictionary/nlp/keywords/unrecognized', { params });
    return response.data?.data || response.data;
  }

  static async nlpAssignArea(keyword, payload = {}) {
    const response = await api.put(`/dictionary/nlp/keywords/${keyword}/assign-area`, payload);
    return response.data?.data || response.data;
  }

  static async templates(params = {}) { return (await api.get('/dictionary/templates', { params })).data?.data || {}; }
  static async createTemplate(payload = {}) { return (await api.post('/dictionary/templates', payload)).data?.data || {}; }
  static async templateFields(templateId) { return (await api.get(`/dictionary/templates/${templateId}/fields`)).data?.data || {}; }
  static async patchTemplateFields(templateId, payload = {}) { return (await api.patch(`/dictionary/templates/${templateId}/fields`, payload)).data?.data || {}; }
  static async deleteTemplateField(templateId, fieldKey) { return (await api.delete(`/dictionary/templates/${templateId}/fields/${fieldKey}`)).data?.data || {}; }

  static async domains(params = {}) { return (await api.get('/dictionary/domains', { params })).data?.data || {}; }
  static async domainEntries(serviceId, params = {}) { return (await api.get(`/dictionary/domains/${serviceId}/entries`, { params })).data?.data || {}; }
  static async domainManifest(serviceId, params = {}) { return (await api.get(`/dictionary/domains/${serviceId}/manifest`, { params })).data?.data || {}; }

  static async questions(params = {}) { return (await api.get('/dictionary/questions', { params })).data?.data || {}; }
  static async questionsFirstFew(params = {}) { return (await api.get('/dictionary/questions/first-few', { params })).data?.data || {}; }
  static async questionsNext(params = {}) { return (await api.get('/dictionary/questions/next', { params })).data?.data || {}; }
  static async questionMappings(id, params = {}) { return (await api.get(`/dictionary/questions/${id}/mappings`, { params })).data?.data || {}; }
  static async createQuestionMapping(id, payload = {}) { return (await api.post(`/dictionary/questions/${id}/mappings`, payload)).data?.data || {}; }
  static async lifecycle(params = {}) { return (await api.get('/dictionary/lifecycle', { params })).data?.data || {}; }
  static async projectBlueprints(params = {}) { return (await api.get('/dictionary/project-blueprints', { params })).data?.data || {}; }
  static async masterBlueprints(params = {}) { return (await api.get('/dictionary/solution/master-blueprints', { params })).data?.data || {}; }
  static async solutionDocumentTypes(params = {}) { return (await api.get('/dictionary/solution/document-types', { params })).data?.data || {}; }
  static async solutionPhases(params = {}) { return (await api.get('/dictionary/solution/phases', { params })).data?.data || {}; }
  static async solutionBrandRouting(params = {}) { return (await api.get('/dictionary/solution/brand-routing', { params })).data?.data || {}; }
  static async solutionOutputSections(params = {}) { return (await api.get('/dictionary/solution/output-sections', { params })).data?.data || {}; }
  static async solutionLearningLoop(params = {}) { return (await api.get('/dictionary/solution/learning-loop', { params })).data?.data || {}; }
  static async solutionGapRegister(params = {}) { return (await api.get('/dictionary/solution/gap-register', { params })).data?.data || {}; }
  static async inferLifecycle(payload = {}) { return (await api.post('/dictionary/solution/infer-lifecycle', payload)).data?.data || {}; }
  static async solutionDocumentPlan(payload = {}) { return (await api.post('/dictionary/solution/document-plan', payload)).data?.data || {}; }
  static async solutionLearn(payload = {}) { return (await api.post('/dictionary/solution/learn', payload)).data?.data || {}; }

  static async atomicActions(params = {}) { return (await api.get('/dictionary/atomic-actions', { params })).data?.data || {}; }
  static async createAtomicAction(payload = {}) { return (await api.post('/dictionary/atomic-actions', payload)).data?.data || {}; }
  static async techProfile(techId) { return (await api.get(`/dictionary/tech/${techId}/profile`)).data?.data || {}; }
  static async patchTechScores(techId, payload = {}) { return (await api.patch(`/dictionary/tech/${techId}/scores`, payload)).data?.data || {}; }
  static async techSearchByAction(action) { return (await api.get('/dictionary/tech/search', { params: { action } })).data?.data || {}; }

  static async uiLabels(params = {}) { return (await api.get('/dictionary/ui/labels', { params })).data?.data || {}; }
  static async uiLegal(params = {}) { return (await api.get('/dictionary/ui/legal', { params })).data?.data || {}; }
  static async archetypes(params = {}) { return (await api.get('/dictionary/archetypes', { params })).data?.data || {}; }
  static async archetypeSections(id, params = {}) { return (await api.get(`/dictionary/archetypes/${id}/sections`, { params })).data?.data || {}; }

  static async aiContextSnippets(entryId) { return (await api.get(`/dictionary/ai/context-snippets/${entryId}`)).data?.data || {}; }
  static async aiSyncMemory(payload = {}) { return (await api.post('/dictionary/ai/sync-memory', payload)).data?.data || {}; }
  static async nlpSynonyms(params = {}) { return (await api.get('/dictionary/nlp/synonyms', { params })).data?.data || {}; }

  static async tuningPropose(payload = {}) { return (await api.post('/dictionary/tuning/propose', payload)).data?.data || {}; }
  static async tuningApply(payload = {}) { return (await api.post('/dictionary/tuning/apply', payload)).data?.data || {}; }
  static async nlpMisses(params = {}) { return (await api.get('/dictionary/nlp/misses', { params })).data?.data || {}; }

  static async health() { return (await api.get('/dictionary/health')).data?.data || {}; }
  static async releaseVersion(payload = {}) { return (await api.post('/dictionary/version/release', payload)).data?.data || {}; }
  static async invalidateCache() { return (await api.post('/dictionary/cache/invalidate', {})).data?.data || {}; }
  static async exportSadTemplate(params = {}) { return (await api.get('/dictionary/export/sad-template', { params })).data?.data || {}; }
  static async categoriesIndex(params = {}) { return (await api.get('/dictionary/categories', { params })).data?.data || {}; }
  static async syncPublic(payload = {}) { return (await api.post('/dictionary/sync/public', payload)).data?.data || {}; }
  static async getGlobalSettings() {
    try {
      return await this.getCategoryEntry('settings', 'dictionary-global', {});
    } catch {
      return null;
    }
  }
  static async saveGlobalSettings(payload = {}) {
    return await this.updateCategoryEntry('settings', 'dictionary-global', {
      entryId: 'dictionary-global',
      locale: null,
      ...payload
    });
  }
  // ── Site branding, metadata, and page content ───────────────────────────
  // Field definitions live server-side in the dictionary's siteContentSchema,
  // so adding an editable field needs no change here or in the editor UI.
  static async siteManifest() {
    const response = await api.get('/dictionary/site/manifest');
    const data = response.data?.data || response.data || {};
    return Array.isArray(data.surfaces) ? data.surfaces : [];
  }

  static async siteSurface(surfaceId) {
    const response = await api.get(`/dictionary/site/surfaces/${surfaceId}`);
    return response.data?.data || response.data;
  }

  static async saveSiteSurface(surfaceId, value = {}) {
    const response = await api.put(`/dictionary/site/surfaces/${surfaceId}`, { value });
    return response.data?.data || response.data;
  }

  // ── Content-managed pages ───────────────────────────────────────────────
  // Admin only. The *public* read is a plain asset fetch — see CmsPage —
  // because a page is an asset document like any other.
  static async sitePageSchema() {
    const response = await api.get('/dictionary/site/pages/_schema');
    return response.data?.data || response.data;
  }

  static async sitePages() {
    const response = await api.get('/dictionary/site/pages');
    const data = response.data?.data || response.data || {};
    return Array.isArray(data.pages) ? data.pages : [];
  }

  static async sitePage(key) {
    const response = await api.get(`/dictionary/site/pages/${key}`);
    return response.data?.data || response.data;
  }

  static async saveSitePage(value = {}) {
    const response = await api.put('/dictionary/site/pages', { value });
    return response.data?.data || response.data;
  }

  static async deleteSitePage(key) {
    const response = await api.delete(`/dictionary/site/pages/${key}`);
    return response.data?.data || response.data;
  }

  static async seedSitePages() {
    const response = await api.post('/dictionary/site/pages/_seed', {});
    return response.data?.data || response.data;
  }

  static async publishSitePages() {
    const response = await api.post('/dictionary/site/pages/_publish', {});
    return response.data?.data || response.data;
  }

  /** The published-page index — every reachable slug. Public, no auth. */
  static async sitePageIndex() {
    const doc = await DictionaryEngine.getPublic('i18n/en/pages/_index');
    return Array.isArray(doc?.PAGES) ? doc.PAGES : [];
  }

  static async getReportStructure() {
    const response = await api.get('/dictionary/report/structure');
    return response.data?.data || response.data;
  }
  static async saveReportStructure(payload = {}) {
    const response = await api.put('/dictionary/report/structure', payload);
    return response.data?.data || response.data;
  }
}

const getPublicAsset = (path) => DictionaryEngine.getPublic(path);

export { DictionaryEngine as dictionary, getPublicAsset };
